import React from 'react';
import {
  Award,
  Calendar,
  CheckCircle2,
  GraduationCap,
  Heart,
  MessageCircle,
  Phone,
  Shield,
  Sparkles,
  Users,
  Zap,
} from 'lucide-react';
import { CLINIC_INFO, TEAM_MEMBERS } from '../data/mock';

const getBookingLabel = (fullName) => {
  const parts = fullName.split(' ');
  const prefix = parts[0];
  const lastName = parts[parts.length - 1];

  if (prefix === 'Dra.') return `Agendar con la Dra. ${lastName}`;
  if (prefix === 'Dr.') return `Agendar con el Dr. ${lastName}`;
  return `Agendar con ${fullName}`;
};

const TeamPage = () => {
  const whatsappBase = `https://wa.me/${CLINIC_INFO.whatsapp}?text=`;

  return (
    <div>
      <section className="bg-white pt-10 pb-8">
        <div className="container">
          <div className="max-w-3xl">
            <p className="text-caption text-[var(--color-accent)] mb-3">Nuestro equipo</p>
            <h1 className="text-h1 mb-4">Especialistas en estética, ortodoncia e implantología</h1>
            <p className="text-body-lg mb-6">
              Un equipo clínico con formación continua y más de {CLINIC_INFO.yearsExperience} años de experiencia
              combinada, enfocado en diagnósticos claros y planes bien explicados.
            </p>

            <div className="flex flex-wrap gap-2 mb-6">
              {['Cédulas visibles', 'Especialistas', `${CLINIC_INFO.yearsExperience}+ años`].map((chip) => (
                <span
                  key={chip}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--bg-secondary)] border border-[var(--border-light)] text-[var(--text-secondary)]"
                >
                  {chip}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={CLINIC_INFO.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <Calendar size={18} />
                Agendar cita
              </a>
              <a href={`tel:${CLINIC_INFO.phone}`} className="btn btn-secondary">
                <Phone size={18} />
                Llamar
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[var(--bg-secondary)] py-8">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Award,
                title: 'Formación continua',
                description: 'Diplomados y certificaciones en México y el extranjero.',
              },
              {
                icon: Heart,
                title: 'Trato humano',
                description: 'Escuchamos, explicamos con calma y respetamos tu decisión.',
              },
              {
                icon: Users,
                title: 'Trabajo integrado',
                description: 'Colaboración entre especialidades para planes más completos.',
              },
            ].map((item) => (
              <div key={item.title} className="card p-6 h-full">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[var(--color-accent-bg)] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={22} className="text-[var(--color-accent)]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[var(--text-primary)] mb-1">{item.title}</h3>
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="container">
          <h2 className="text-h2 mb-10 text-center">Conoce a nuestros especialistas</h2>

          <div className="space-y-14">
            {TEAM_MEMBERS.map((member, index) => {
              const isEven = index % 2 === 0;
              const whatsappMessage = encodeURIComponent(
                `Hola, me gustaría agendar una cita con ${member.name}. Mi nombre es ___, mi disponibilidad es ___.`
              );

              return (
                <div
                  key={member.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center ${
                    isEven ? '' : 'lg:[direction:rtl]'
                  }`}
                >
                  <div className={isEven ? '' : 'lg:[direction:ltr]'}>
                    <div className="aspect-[3/4] max-w-md mx-auto rounded-2xl overflow-hidden shadow-lg">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                    {/* TODO: Reemplazar con fotos reales uniformadas (misma iluminación y fondo). */}
                  </div>

                  <div className={isEven ? '' : 'lg:[direction:ltr]'}>
                    <div className="mb-5">
                      <p className="text-caption text-[var(--color-accent)] mb-2">{member.role}</p>
                      <h3 className="text-h2 mb-1">{member.name}</h3>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <span className="px-3 py-1.5 rounded-full text-xs bg-[var(--bg-secondary)] border border-[var(--border-light)] text-[var(--text-secondary)]">
                          {member.specialty}
                        </span>
                        <span className="px-3 py-1.5 rounded-full text-xs bg-[var(--bg-secondary)] border border-[var(--border-light)] text-[var(--text-secondary)]">
                          {member.experience} de experiencia
                        </span>
                        <span className="px-3 py-1.5 rounded-full text-xs bg-[var(--bg-secondary)] border border-[var(--border-light)] text-[var(--text-secondary)]">
                          {member.cedula}
                        </span>
                      </div>
                      {/* TODO: Agregar prueba social por especialista (rating/reseñas) cuando exista una fuente real. */}
                    </div>

                    <div className="space-y-3 mb-6">
                      {[
                        { label: 'Enfoque', value: member.focus },
                        { label: 'Tratamientos principales', value: member.mainTreatments.join(' • ') },
                        { label: 'Lo que lo distingue', value: member.differential },
                      ].map((row) => (
                        <div key={row.label} className="flex items-start gap-3">
                          <CheckCircle2 size={18} className="text-[var(--color-accent)] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-[var(--text-primary)]">{row.label}</p>
                            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{row.value}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="rounded-xl p-5 mb-6 bg-white border border-[var(--border-light)]">
                      <div className="flex items-center gap-2 mb-3">
                        <GraduationCap size={18} className="text-[var(--color-accent)]" />
                        <p className="text-sm font-semibold text-[var(--text-primary)]">Formación</p>
                      </div>
                      <ul className="space-y-1.5">
                        {member.education.map((edu) => (
                          <li key={edu} className="text-sm text-[var(--text-secondary)] flex items-start gap-2">
                            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[var(--color-accent)] flex-shrink-0" />
                            <span>{edu}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href={CLINIC_INFO.calendlyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary justify-center"
                      >
                        <Calendar size={18} />
                        {getBookingLabel(member.name)}
                      </a>
                      <a href={`tel:${CLINIC_INFO.phone}`} className="btn btn-secondary justify-center">
                        <Phone size={18} />
                        Llamar
                      </a>
                      <a
                        href={`${whatsappBase}${whatsappMessage}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-whatsapp justify-center sm:ml-auto"
                      >
                        <MessageCircle size={18} />
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[var(--bg-secondary)] py-12">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-h2 mb-4">Tecnología y protocolos</h2>
            <p className="text-body-lg">
              Invertimos en equipos modernos y seguimos protocolos estrictos para tu seguridad y comodidad.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: Shield,
                title: 'Esterilización certificada',
                description: 'Autoclave y protocolos de bioseguridad que cumplen normas vigentes.',
              },
              {
                icon: Zap,
                title: 'Radiografías digitales',
                description: 'Menor radiación que métodos tradicionales y resultados inmediatos.',
              },
              {
                icon: Sparkles,
                title: 'Escáner intraoral 3D',
                description: 'Moldes digitales sin incomodidad para ortodoncia y prótesis.',
              },
              {
                icon: Award,
                title: 'Planificación digital',
                description: 'Simulación de resultados antes de iniciar para decisiones claras.',
              },
            ].map((item) => (
              <div key={item.title} className="card p-6 text-center h-full flex flex-col">
                <div className="w-12 h-12 mx-auto mb-4 bg-[var(--color-accent-bg)] rounded-xl flex items-center justify-center">
                  <item.icon size={22} className="text-[var(--color-accent)]" />
                </div>
                <h3 className="font-semibold text-[var(--text-primary)] mb-2">{item.title}</h3>
                <p className="text-sm text-[var(--text-muted)] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-h2 mb-8 text-center">Nuestro compromiso contigo</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  icon: Heart,
                  title: 'Escuchamos primero',
                  description: 'Entendemos tus objetivos y tu experiencia previa antes de proponer opciones.',
                },
                {
                  icon: Sparkles,
                  title: 'Explicamos con claridad',
                  description: 'Te mostramos el plan y el porqué, con un lenguaje simple y sin presiones.',
                },
                {
                  icon: Shield,
                  title: 'Transparencia en costos',
                  description: 'Recibes presupuesto por escrito antes de iniciar, sin sorpresas.',
                },
                {
                  icon: Users,
                  title: 'Acompañamiento real',
                  description: 'Seguimiento y comunicación durante el proceso para que avances con confianza.',
                },
              ].map((item) => (
                <div key={item.title} className="card p-6 h-full">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[var(--color-accent-bg)] rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon size={22} className="text-[var(--color-accent)]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[var(--text-primary)] mb-1">{item.title}</h3>
                      <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <a href={CLINIC_INFO.calendlyUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                <Calendar size={18} />
                Agendar cita
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[var(--bg-dark)] text-white py-12">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-h2 text-white mb-3">Agenda tu cita con el equipo</h2>
            <p className="text-gray-300 text-base mb-6">
              Incluye: evaluación, diagnóstico y plan por escrito. Conoce la clínica, resuelve dudas y decide con calma.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={CLINIC_INFO.calendlyUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                <Calendar size={20} />
                Agendar cita
              </a>
              <a href={`tel:${CLINIC_INFO.phone}`} className="btn btn-secondary btn-lg">
                <Phone size={20} />
                Llamar
              </a>
            </div>

            {/* TODO: Si es cierto, especificar tiempo de respuesta por WhatsApp (ej. “Respuesta en X min”). */}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
